import React, { useState } from 'react';
import { View, Text, Pressable, StyleSheet, useWindowDimensions } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';

interface FieldItemProps {
  label: string;
  value?: string;
  iconName: keyof typeof MaterialCommunityIcons.glyphMap;
  isEditing?: boolean;
  onPress?: () => void;
  verified?: boolean;
  containerStyle?: any;
  children?: React.ReactNode;
}

const FieldItem: React.FC<FieldItemProps> = ({
  label,
  value,
  iconName,
  isEditing = false,
  onPress,
  verified = false,
  containerStyle,
  children,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const { width: winW } = useWindowDimensions();
  const itemWidth = winW < 720 ? '100%' : '48%';

  return (
    <Pressable
      style={[
        localStyles.gridItem,
        { width: itemWidth },
        containerStyle,
        isHovered && isEditing && localStyles.gridItemHover
      ]}
      onPress={isEditing ? onPress : undefined}
      onHoverIn={() => isEditing && setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      disabled={!isEditing}
    >
      <View style={[localStyles.iconContainer, isHovered && isEditing && localStyles.iconContainerHover]}>
        <MaterialCommunityIcons
          name={iconName}
          size={20}
          color={isHovered && isEditing ? '#3C8248' : '#64748B'}
        />
      </View>
      <View style={localStyles.inputWrapper}>
        <View style={localStyles.fieldHeader}>
          <Text style={localStyles.fieldLabel}>{label}</Text>
          {verified && (
            <View style={localStyles.verifiedBadge}>
              <MaterialCommunityIcons name="check-circle" size={14} color="#3C8248" />
              <Text style={localStyles.verifiedText}>Verified</Text>
            </View>
          )}
        </View>
        {children ? (
          children
        ) : (
          <View style={localStyles.fieldContent}>
            <Text
              style={[
                localStyles.displayText,
                !value && localStyles.placeholderText
              ]}
              numberOfLines={1}
            >
              {value || 'Not provided'}
            </Text>
            {isEditing && (
              <View style={localStyles.editIndicator}>
                <MaterialCommunityIcons name="chevron-right" size={18} color="#94A3B8" />
              </View>
            )}
          </View>
        )}
      </View>
    </Pressable>
  );
};

export default FieldItem;

const localStyles = StyleSheet.create({
  gridItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    paddingVertical: 14,
    paddingHorizontal: 16,
    backgroundColor: '#FFFFFF',
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#F1F5F9',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.02,
    shadowRadius: 2,
    elevation: 1,
  },
  gridItemHover: {
    backgroundColor: '#F8FAFC',
    borderColor: '#E2E8F0',
    transform: [{ translateY: -2 }],
    shadowOpacity: 0.05,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 4 },
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
  },
  iconContainerHover: {
    backgroundColor: '#E8F5E8',
  },
  inputWrapper: {
    flex: 1,
    minWidth: 0,
  },
  fieldHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#64748B',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  verifiedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E8F5E8',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 20,
    gap: 4,
  },
  verifiedText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#3C8248',
  },
  fieldContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  displayText: {
    fontSize: 15,
    fontWeight: '500',
    color: '#1E293B',
    flex: 1,
  },
  placeholderText: {
    color: '#94A3B8',
    fontStyle: 'italic',
  },
  editIndicator: {
    marginLeft: 8,
  },
});


